<template>
	<div class="economic-h5">
		<div class="detail-infos m8">分析使用的经济参数如下表：</div>

		<div class="info-box">
			<div class="line" v-for="(item, index) in infos" :key="index">
				<div class="label">{{ item.name }}</div>
				<div class="value">{{ toFixed(infosData[item.props]) }}{{ item.unit }}</div>
			</div>
		</div>

		<!-- 税 -->
		<div v-if="hasTax" class="info-box">
			<div class="line tax" v-for="(item, index) in taxs" :key="index">
				<div class="label">{{ item.name }}</div>
				<div class="value">{{ toFixed(taxsData[item.props]) }}{{ item.unit }}</div>
			</div>
		</div>

		<!-- 计算结果得出 -->
		<div class="info-box m24">
			<div class="line" v-for="(item, index) in analysis" :key="index">
				<div class="label">{{ item.name }}</div>
				<div class="value">{{ toFixed(analysisData[item.props], item.toFixed) }}{{ item.unit }}</div>
			</div>
		</div>

		<!-- 历年放电收益 -->
		<history-income-table :yearProfitInfos="yearProfitInfosData" :investorCashFlows="investorCashFlowsData" :hasTax="hasTax"></history-income-table>
	</div>
</template>

<script setup>
import HistoryIncomeTable from "./HistoryIncomeTable.vue";
import {ref, defineProps, watch} from "vue";
import {toFixed} from "@/plugins/index.js";

const props = defineProps({
	economicInfo: {
		type: Object,
		default: () => {
			return null;
		},
	},
});

const infos = [
	{
		name: "储能单价",
		unit: "元/Wh",
		props: "unitPrice",
	},
	{
		name: "年运营成本占建设成本",
		unit: "%",
		props: "operationCost",
	},
	{
		name: "储能寿命",
		unit: "年",
		props: "lifeYear",
	},
	{
		name: "年运行天数",
		unit: "天",
		props: "yearRunDays",
	},
	{
		name: "年衰减率",
		unit: "%",
		props: "annualDecayRate",
	},
	{
		name: "前5年投资方分成",
		unit: "%",
		props: "earlyPercent",
	},
	{
		name: "5年后投资方分成",
		unit: "%",
		props: "laterPercent",
	},
];
const infosData = ref({
	unitPrice: undefined,
	operationCost: undefined,
	lifeYear: undefined,
	yearRunDays: undefined,
	annualDecayRate: undefined,
	earlyPercent: undefined,
	yearThreshold: undefined,
	laterPercent: undefined,
});

const taxs = [
	{
		name: "进项税：建设期设备材料费税率",
		unit: "%",
		props: "buildInputTax",
	},
	{
		name: "进项税：经营期运营费税率",
		unit: "%",
		props: "operationInputTax",
	},
	{
		name: "销项税：现金流入税率",
		unit: "%",
		props: "outputTax",
	},
	{
		name: "设备折旧年限",
		unit: "年",
		props: "yearDepreciation",
	},
	{
		name: "所得税税率",
		unit: "%",
		props: "incomeTax",
	},
];
const taxsData = ref({
	buildInputTax: undefined,
	operationInputTax: undefined,
	outputTax: undefined,
	yearDepreciation: undefined,
	incomeTax: undefined,
});
const hasTax = ref(true);

const analysis = [
	{
		name: "单位容量日均收益",
		unit: "元/(天*kWh)",
		props: "unitCapacityDayProfit",
		toFixed: 4,
	},
	{
		name: "日均收益",
		unit: "元/天",
		props: "dayProfit",
		toFixed: 2,
	},
	{
		name: "内部收益率 IRR",
		unit: "%",
		props: "irr",
		toFixed: 2,
	},
	{
		name: "储能度电成本 LCOS",
		unit: "元/kWh",
		props: "lcos",
		toFixed: 4,
	},
];

const analysisData = ref({
	dayProfit: undefined,
	unitCapacityDayProfit: undefined,
	irr: undefined,
	lcos: undefined,
});

// 表格数据
const yearProfitInfosData = ref([]);
const investorCashFlowsData = ref([]);

// 生成顶部数据
const makeTopInfos = (info = {}) => {
	const {economicParam, taxParam} = info;
	if (economicParam) {
		infosData.value = economicParam;
	}

	if (taxParam) {
		hasTax.value = true;
		taxsData.value = taxParam;
	} else {
		hasTax.value = false;
	}
};

// 生成底部数据
const makeBottomInfos = (info = {}) => {
	const {economicAnalysisResult, yearProfitInfos, investorCashFlows} = info;
	if (economicAnalysisResult) {
		analysisData.value = economicAnalysisResult;
	}

	if (yearProfitInfos) {
		yearProfitInfosData.value = yearProfitInfos;
	}
	if (investorCashFlows) {
		investorCashFlowsData.value = investorCashFlows;
	}
};

watch(
	() => props.economicInfo,
	newVal => {
		if (newVal) {
			console.log(newVal);
			const {economicAnalysisVO, economicAnalysisResultVO} = newVal;
			// 生成顶部信息栏
			makeTopInfos(economicAnalysisVO);
			// 生成底部信息
			makeBottomInfos(economicAnalysisResultVO);
		}
	},
	{immediate: true, deep: true}
);
</script>

<style lang="less" scoped>
.economic-h5 {
	padding-top: 20px;

	.info-box {
		border: 1px solid #a2abbb66;
		border-radius: 4px;
		margin-bottom: 16px;
		.line {
			display: flex;
			border-bottom: 1px solid #a2abbb66;

			&:last-child {
				border-bottom: unset;
			}

			font-size: 14px;
			color: #000000;
			letter-spacing: 0;
			.label {
				flex: 1;
				padding: 9px 12px;
				background: #e9eff666;
				color: rgba(0, 0, 0, 0.65);
				font-family: PingFangSC-Regular;
				font-weight: 400;
				border-right: 1px solid rgba(162, 171, 187, 0.4);
			}
			.value {
				flex: 1;
				padding: 9px 12px;
				opacity: 0.85;
				font-family: PingFangSC-Medium;
				font-weight: 600;
			}
		}

		.tax {
			.label {
				flex: 6;
			}
			.value {
				flex: 2;
			}
		}
	}
	.m8 {
		margin-bottom: 8px;
	}
	.m24 {
		margin-bottom: 24px;
	}
}
</style>
